import {NodeOutputData, NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import getMatchingEnumValue from 'common/src/utils/typescriptUtils/getMatchingEnumValue';
import {z} from 'zod';
import {DTCreativeNode, DTNode} from '../../decisionTree/decisionTree.types';
import {CampaignMetric} from '../campaignBrief/campaignBrief.enums';
import {CampaignMiniBrief} from '../campaignBrief/campaignBrief.types';
import {AiCampaignMetric, AiDSP, DSP} from './copilotCampaigns.enums';
import {conceptNodeOutputSchema} from './copilotCampaigns.schemas';
import {AiCampaignMiniBrief, AiNodeType, AiOutputType, ConceptNode} from './copilotCampaigns.types';

function toAiCampaignMiniBrief(currentBrief: CampaignMiniBrief): AiCampaignMiniBrief {
  const {metricMeasure, dspIds, campaignBudgetScope, audiences, objective, endDate, startDate} =
    currentBrief;

  const aiStartDate = startDate ? startDate.toISO() : null;
  const aiEndDate = endDate ? endDate.toISO() : null;

  const aiMetricMeasure = (metricMeasure ?? []).map((metric: CampaignMetric) =>
    getMatchingEnumValue(CampaignMetric, AiCampaignMetric, metric),
  );

  const aiDspIds = (dspIds ?? []).map((dspId: number) => getMatchingEnumValue(DSP, AiDSP, dspId));

  const aiMiniBrief: AiCampaignMiniBrief = {
    startDate: aiStartDate,
    endDate: aiEndDate,
    objective: objective,
    audiences: audiences ?? null,
    dspIds: aiDspIds,
    campaignBudgetScope,
    metricMeasure: aiMetricMeasure,
  };

  return aiMiniBrief;
}

function toConceptNode(sourceNode: DTNode): ConceptNode {
  const {id, name, description} = sourceNode;

  const conceptNode: Partial<ConceptNode> = {
    type: getMatchingEnumValue(NodeType, AiNodeType, sourceNode.type),
    id,
    name,
    description,
  };

  conceptNode.outputs = sourceNode.outputs.map(toConceptNodeOutput);

  if (sourceNode.type === NodeType.CREATIVE) {
    const {requireLocation, requireProduct} = sourceNode as DTCreativeNode;
    conceptNode.requireLocation = requireLocation;
    conceptNode.requireProduct = requireProduct;
  }

  return conceptNode;
}

function toConceptNodeOutput(
  dtNodeOutput: NodeOutputData,
): z.infer<typeof conceptNodeOutputSchema> {
  const {id, name, nodeId, type} = dtNodeOutput;

  return {
    id,
    name,
    nodeId,
    type: getMatchingEnumValue(OutputType, AiOutputType, type || OutputType.Regular),
  };
}

function toDtNode(conceptNode: ConceptNode, id: number): DTNode {
  const {name, description, depth, outputs} = conceptNode;

  const dtNode: DTNode = {
    id,
    type: getMatchingEnumValue(AiNodeType, NodeType, conceptNode.type),
    name,
    description,
    // signalDescription,
    x: (depth || 0) * 400,
    y: 0,
    inputs: [],
    outputs: outputs.map((output) => toDtNodeOutput(output)),
  };

  if (conceptNode.type === AiNodeType.CREATIVE) {
    const {requireLocation, requireProduct} = conceptNode;
    const creativeNode = {...dtNode, requireLocation, requireProduct};

    return creativeNode;
  }

  return dtNode;
}

function toDtNodeOutput(
  conceptNodeOutput: z.infer<typeof conceptNodeOutputSchema>,
): NodeOutputData {
  const {name, id, type, nodeId} = conceptNodeOutput;

  return {
    type: getMatchingEnumValue(AiOutputType, OutputType, type),
    nodeId,
    id,
    name,
  };
}

export {toAiCampaignMiniBrief, toConceptNode, toConceptNodeOutput, toDtNode, toDtNodeOutput};
